import { JSX, ReactElement, ReactNode } from 'react';
import {
    DateField,
    ReferenceField,
    Show,
    useRecordContext,
    WithRecord,
} from 'react-admin';
import {
    Box,
    Grid,
    LinearProgress,
    Link,
    Typography,
} from '@mui/material';
import { useStripeModeFetch } from "../global-hooks/default-system-hooks";
import { SubscriptionRefund } from "../../types/entities";
import { FormSection } from "../edit/components/FormSection";
import { HQ_MONO } from "../../styles/theme/hqTheme";
import { RefundStatusChip } from "./components/RefundStatusChip";
import { formatRefundAmount } from "./components/refund-format";

const STRIPE_DASHBOARD: string = 'https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/';

const RefundTitle: () => JSX.Element | null = (): JSX.Element | null => {
    const record: SubscriptionRefund | undefined = useRecordContext<SubscriptionRefund>();
    if (!record) return null;
    return <span>{ "Refund #" + record.id }</span>
}

/** A label on the left and its value on the right, as the Noks edit page shows read-only values. */
function DetailRow(props: { label: string, mono?: boolean, children: ReactNode }): JSX.Element {
    return (
        <Box sx={ {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 2,
            padding: '10px 0',
            borderBottom: '1px solid',
            borderColor: 'divider',
        } }>
            <Typography variant="body2" sx={ { color: 'text.secondary', flexShrink: 0 } }>
                { props.label }
            </Typography>
            <Box sx={ {
                textAlign: 'right',
                wordBreak: 'break-all',
                fontWeight: 600,
                fontSize: '14px',
                fontFamily: props.mono ? HQ_MONO : undefined,
            } }>
                { props.children }
            </Box>
        </Box>
    );
}

export const SubscriptionRefundShow: () => JSX.Element = (): JSX.Element => {
    const { isTestMode, loadingTestModeData } = useStripeModeFetch();

    function stripeLink(path: string, id: string | null | undefined): ReactNode {
        if (!id) {
            return <Typography component="span" variant="body2" sx={ { color: 'text.secondary' } }>{ "Not recorded" }</Typography>;
        }
        if (loadingTestModeData) {
            return <LinearProgress sx={ { width: '160px', display: 'inline-block' } } />;
        }
        return (
            <Link href={ STRIPE_DASHBOARD + (isTestMode ? 'test/' : '') + path + '/' + id }
                  target="_blank"
                  rel="noopener noreferrer"
            >{ id }</Link>
        );
    }

    return (
        // No actions: a refund is Stripe's record of money that has moved, and there is no edit
        // screen for it. The Edit button that was here led nowhere.
        <Show actions={ false } title={ <RefundTitle /> }>
            <WithRecord render={ (record: SubscriptionRefund): ReactElement => (
                <Box sx={ { padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: '32px' } }>
                    <FormSection title="Refund">
                        <Grid container columnSpacing={ 4 } rowSpacing={ 2 }>
                            <Grid size={ { xs: 12, md: 6 } }>
                                <Typography variant="body2" sx={ { color: 'text.secondary' } }>{ "Amount refunded" }</Typography>
                                <Box sx={ { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '4px' } }>
                                    <Typography sx={ { fontSize: '32px', fontWeight: 700, lineHeight: 1.2 } }>
                                        { formatRefundAmount(record.currency, record.amount) }
                                    </Typography>
                                    <RefundStatusChip status={ record.status } />
                                </Box>
                            </Grid>
                            <Grid size={ { xs: 12, md: 6 } }>
                                <DetailRow label="Refunded at"><DateField source="createdAt" showTime /></DetailRow>
                                <DetailRow label="Currency">{ String(record.currency ?? '').toUpperCase() }</DetailRow>
                                <DetailRow label="Invoice">
                                    <ReferenceField source="invoice" reference="subscription_invoices" link="show" />
                                </DetailRow>
                            </Grid>
                        </Grid>
                    </FormSection>

                    <FormSection title="Reason">
                        { (record.notes)
                            ? <Typography variant="body1" sx={ { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }>{ record.notes }</Typography>
                            : <Typography variant="body2" sx={ { color: 'text.secondary' } }>{ "No notes were recorded for this refund." }</Typography>
                        }
                    </FormSection>

                    <FormSection title="Stripe" hint="Each ID opens in the Stripe dashboard.">
                        <DetailRow label="Refund ID" mono>{ stripeLink('refunds', record.stripeId) }</DetailRow>
                        <DetailRow label="Payment intent ID" mono>{ stripeLink('payments', record.paymentIntentId) }</DetailRow>
                        <DetailRow label="Charge ID" mono>{ stripeLink('charges', record.chargeId) }</DetailRow>
                    </FormSection>
                </Box>
            ) } />
        </Show>
    );
}
